import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// Demo mode state (set by DemoContext)
let _isDemoMode = false;

export function setDemoModeForApi(isDemo) {
  _isDemoMode = isDemo;
}

// Item 82 — active entity (set by EntityContext). null = default entity
// (no header sent → backend takes the no-swap fast path, byte-identical to
// pre-entity behaviour).
let _activeEntityId = null;

export function setActiveEntityForApi(entityId) {
  _activeEntityId = entityId || null;
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// ── One write per click (20 Sep 2026 sign-up audit) ─────────────────────────
// `disabled={loading}` on a button only takes effect after React re-renders,
// so a double-click sends the same save twice: two broker accounts from
// Settings → Add Account, two from the setup wizard, two registrations. Buttons
// were being patched one at a time; this closes the whole class at the door.
// While a mutating request is in flight, an IDENTICAL one (same method, URL,
// params and body) is not sent again — the second caller simply shares the
// first one's response. A different body, or the same request after the first
// has finished, goes through untouched. GETs are never touched.
const _inflightWrites = new Map();
const _describeBody = (data) => {
  if (data == null) return '';
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const parts = [];
    for (const [k, v] of data.entries()) {
      parts.push(typeof v === 'string' ? `${k}=${v}` : `${k}=file:${v.name}:${v.size}:${v.lastModified}`);
    }
    return `form:${parts.join('&')}`;
  }
  if (typeof data === 'string') return data;
  try { return JSON.stringify(data); } catch (_) { return null; }   // null = can't describe → never dedupe
};
const _baseAdapter = axios.getAdapter(axios.defaults.adapter);
// Identical GETs in flight at the same moment also share one request (24 Sep
// 2026 speed audit). The dashboard's six loaders asked for the current NAV
// snapshot twice and the open trades twice in the same instant, and the
// reconciliation page asked for the review queue twice — each a full server
// computation (seconds for a busy account) for a response the page already
// had coming. This is NOT a cache: only a request still pending is shared;
// the next call after it settles goes to the server again.
const _inflightReads = new Map();
api.defaults.adapter = (config) => {
  const method = String(config.method || 'get').toLowerCase();
  if (method === 'get') {
    const key = [config.baseURL || '', config.url || '', JSON.stringify(config.params || {}),
      config.headers?.['X-Active-Entity'] || '', config.headers?.['X-Demo-Mode'] || '',
      config.headers?.Authorization || ''].join('|');
    const existing = _inflightReads.get(key);
    if (existing) return existing;
    const p = _baseAdapter(config).finally(() => { _inflightReads.delete(key); });
    _inflightReads.set(key, p);
    return p;
  }
  if (method === 'head' || method === 'options') return _baseAdapter(config);
  const body = _describeBody(config.data);
  if (body === null) return _baseAdapter(config);
  const key = [method, config.baseURL || '', config.url || '', JSON.stringify(config.params || {}),
    config.headers?.['X-Active-Entity'] || '', body].join('|');
  const existing = _inflightWrites.get(key);
  if (existing) return existing;
  const p = _baseAdapter(config).finally(() => { _inflightWrites.delete(key); });
  _inflightWrites.set(key, p);
  return p;
};

// Request interceptor to add auth token and demo mode param
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // A token carrying characters outside Latin-1 (e.g. a stray curly quote or
      // non-breaking space from a copy-paste) makes setRequestHeader throw and
      // breaks EVERY request with a cryptic "non ISO-8859-1 code point" error.
      // A valid JWT is printable ASCII (base64url + dots), so treat anything
      // else as corrupt: drop it and fall back to login instead of hard-failing.
      if (/^[!-~]+$/.test(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    // Append demoMode=true for GET requests when demo mode is active
    if (_isDemoMode && config.method === 'get') {
      config.params = {
        ...config.params,
        demoMode: 'true'
      };
    }

    // Platform audit 2026-06-28 (H4): while demo mode is active, EVERY
    // request carries this header. The backend rejects non-GET requests that
    // carry it (403 demo_read_only) — defense in depth so a mutation button
    // that was missed by UI demo-gating can never write to the real account
    // while the user believes they're in a sandbox.
    if (_isDemoMode) {
      config.headers['X-Demo-Mode'] = 'true';
    }

    // Item 82 — every request acts as the ACTIVE ENTITY (reads AND writes:
    // an import while in the SMSF must land in the SMSF). Demo mode wins if
    // both are somehow set (demo swaps after entities server-side anyway).
    if (_activeEntityId && !_isDemoMode) {
      config.headers['X-Active-Entity'] = _activeEntityId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login if not already on a public auth page
      const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
      if (!publicPaths.includes(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
